import { PillTheme } from "@redotech/redo-model/pill-theme";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import * as z from "zod/v4";

export enum GlobalSearchHistoryItemType {
  PAGE = "page",
  DOCUMENTATION = "documentation",
  ORDER = "order",
  RETURN = "return",
  CONVERSATION = "conversation",
  CUSTOMER = "customer",
}

const baseSearchItem = z.object({
  id: z.string(),
  type: z.enum(
    Object.values(GlobalSearchHistoryItemType) as [string, ...string[]],
  ),
});

const orderSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.ORDER),
  orderName: z.string(),
  customerName: z.string().nullish(),
  orderCreatedAt: z.date(),
  price: z.number(),
  items: z.number().int(),
  fulfillmentStatus: z.string(),
});
export const orderSearchItem: z.ZodType<OrderSearchItem> = orderSearchItem_;
export type OrderSearchItem = {} & z.infer<typeof orderSearchItem_>;

const customerSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.CUSTOMER),
  shopifyId: z.string().nullish(),
  customerName: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
});
export const customerSearchItem: z.ZodType<CustomerSearchItem> =
  customerSearchItem_;
export type CustomerSearchItem = {} & z.infer<typeof customerSearchItem_>;

const returnSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.RETURN),
  orderName: z.string(),
  returnType: z.string(),
  returnStatus: z.nativeEnum(ReturnStatus),
  customerName: z.string().nullish(),
  returnStartedAtDate: z.date(),
  returnValue: z.number().nullish(),
});
export const returnSearchItem: z.ZodType<ReturnSearchItem> = returnSearchItem_;
export type ReturnSearchItem = {} & z.infer<typeof returnSearchItem_>;

const conversationSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.CONVERSATION),
  customer: z.string(),
  createdAt: z.date(),
  subject: z.string().nullish(),
  platform: z.string(),
  tags: z
    .array(z.object({ text: z.string(), color: z.nativeEnum(PillTheme) }))
    .default([]),
});
export const conversationSearchItem: z.ZodType<ConversationSearchItem> =
  conversationSearchItem_;
export type ConversationSearchItem = {} & z.infer<
  typeof conversationSearchItem_
>;

const pageSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.PAGE),
  breadcrumbs: z.array(z.string()),
  description: z.string().nullish(),
});
export const pageSearchItem: z.ZodType<PageSearchItem> = pageSearchItem_;
export type PageSearchItem = {} & z.infer<typeof pageSearchItem_>;

const documentationSearchItem_ = baseSearchItem.extend({
  type: z.literal(GlobalSearchHistoryItemType.DOCUMENTATION),
  title: z.string(),
  description: z.string().nullish(),
  url: z.string(),
});
export const documentationSearchItem: z.ZodType<DocumentationSearchItem> =
  documentationSearchItem_;
export type DocumentationSearchItem = {} & z.infer<
  typeof documentationSearchItem_
>;

export const GlobalSearchItemSchema = z.discriminatedUnion("type", [
  orderSearchItem_,
  pageSearchItem_,
  returnSearchItem_,
  conversationSearchItem_,
  documentationSearchItem_,
  customerSearchItem_,
]);
export type GlobalSearchItem = z.infer<typeof GlobalSearchItemSchema>;
